import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsRole } from './api';
import { SectionCard } from './shared';

interface FunnelRow { stageId: number; name: string; color: string | null; isPlaced: boolean; reached: number; conversion: number }
interface TimeRow { stageId: number; name: string; color: string | null; avgDays: number | null; samples: number }
interface RejRow { reason: string; rejectedBy: string; n: number }
interface Reports { totalCards: number; funnel: FunnelRow[]; timeInStage: TimeRow[]; rejections: RejRow[] }

const REJECTED_BY_LABEL: Record<string, string> = { client: 'By client', us: 'By us', candidate: 'Candidate withdrew' };

// Pipeline analytics computed from the stage-move event stream: funnel
// conversion, average time per stage, rejection reasons. Filterable to one
// role or across everything.
const AtsReports: React.FC = () => {
  const { token } = useAuth();
  const [roles, setRoles] = useState<AtsRole[]>([]);
  const [roleId, setRoleId] = useState('');
  const [data, setData] = useState<Reports | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    atsFetch(token, '/roles').then((d) => setRoles(d.roles)).catch(() => {});
  }, [token]);

  const load = useCallback(() => {
    if (!token) return;
    atsFetch(token, `/reports${roleId ? `?roleId=${roleId}` : ''}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [token, roleId]);

  useEffect(load, [load]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;

  const maxReached = Math.max(1, ...data.funnel.map((f) => f.reached));
  const maxDays = Math.max(1, ...data.timeInStage.map((t) => t.avgDays || 0));
  const totalRejections = data.rejections.reduce((s, r) => s + r.n, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <select value={roleId} onChange={(e) => setRoleId(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 max-w-xs">
          <option value="">All roles ({data.totalCards} candidates)</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.title} — {r.client_name}</option>)}
        </select>
      </div>

      {data.totalCards === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 mb-1">Nothing to report yet.</p>
          <p className="text-xs text-gray-400">Reports build themselves from pipeline movement — add candidates to a role and start moving them.</p>
        </div>
      ) : (
        <>
          <SectionCard title="Funnel — how far candidates get">
            <div className="space-y-2.5">
              {data.funnel.map((f) => (
                <div key={f.stageId} className="flex items-center gap-3">
                  <span className="w-24 text-xs text-gray-500 truncate">{f.name}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div
                      className="h-full rounded-full flex items-center justify-between px-2.5"
                      style={{ width: `${Math.max(8, (f.reached / maxReached) * 100)}%`, backgroundColor: f.color || '#3B82F6' }}
                    >
                      <span className="text-[10px] font-semibold text-white">{f.reached}</span>
                    </div>
                  </div>
                  <span className="w-12 text-right text-xs font-medium text-gray-500">{f.conversion}%</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-3">A candidate counts for every stage they reached, so each bar reads "got at least this far". Percentages are against everyone who entered the pipeline.</p>
          </SectionCard>

          <div className="grid lg:grid-cols-2 gap-6">
            <SectionCard title="Average time in stage">
              {data.timeInStage.every((t) => t.avgDays === null) ? (
                <p className="text-sm text-gray-400">Not enough movement yet — this fills in as candidates progress.</p>
              ) : (
                <div className="space-y-2.5">
                  {data.timeInStage.map((t) => (
                    <div key={t.stageId} className="flex items-center gap-3">
                      <span className="w-24 text-xs text-gray-500 truncate">{t.name}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                        {t.avgDays !== null && (
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${Math.max(6, (t.avgDays / maxDays) * 100)}%`, backgroundColor: t.color || '#8B5CF6' }}
                          />
                        )}
                      </div>
                      <span className="w-16 text-right text-xs text-gray-500">{t.avgDays !== null ? `${t.avgDays}d` : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-gray-400 mt-3">Long bars are where pipelines stall — the first place to look when a role drags.</p>
            </SectionCard>

            <SectionCard title={`Rejection reasons (${totalRejections})`}>
              {data.rejections.length === 0 ? (
                <p className="text-sm text-gray-400">No rejections logged yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="py-2 font-medium">Reason</th>
                      <th className="py-2 font-medium">Who</th>
                      <th className="py-2 font-medium text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rejections.map((r, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 text-gray-700">{r.reason}</td>
                        <td className="py-2 text-xs text-gray-500">{REJECTED_BY_LABEL[r.rejectedBy] || r.rejectedBy}</td>
                        <td className="py-2 text-right font-medium text-gray-700">{r.n}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
};

export default AtsReports;
